/**
 * React Query hooks for `api/knowledge-bases`. Query-key strategy per
 * `aidlc-docs/construction/knowledge-base/nfr-design/pr-d-dashboard.md` § 1.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "./client";
import type {
  CreateKnowledgeBaseRequest,
  KnowledgeBase,
  ListKbParams,
  Paged,
  UpdateKnowledgeBaseRequest,
} from "./types";

export const kbKeys = {
  all: (slug: string) => ["kb", slug] as const,
  list: (slug: string, params?: ListKbParams) => ["kb", slug, "list", params ?? {}] as const,
  detail: (slug: string, id: string) => ["kb", slug, "detail", id] as const,
};

export function useKnowledgeBases(slug: string, params: ListKbParams = {}) {
  return useQuery({
    queryKey: kbKeys.list(slug, params),
    queryFn: () =>
      apiRequest<Paged<KnowledgeBase>>("GET", "/api/knowledge-bases", undefined, {
        cursor: params.cursor,
        limit: params.limit,
        q: params.q,
      }),
  });
}

/** Minimal KB shape returned by the reachable-KBs endpoint. */
export interface ReachableKb {
  id: string;
  name: string;
  slug: string;
}

/**
 * Knowledge bases reachable from an agent — the agent's own attached KBs plus,
 * for a Master, its Workers' KBs. Used to scope the channel suggestion KB
 * picker to what the channel's entry agent can actually use.
 */
export function useReachableKnowledgeBases(slug: string, agentId: string | undefined) {
  return useQuery({
    queryKey: ["kb", slug, "reachable", agentId ?? ""] as const,
    queryFn: () =>
      apiRequest<{ items: ReachableKb[] }>(
        "GET",
        `/api/agents/${agentId}/knowledge-bases/reachable`,
      ),
    enabled: Boolean(agentId),
  });
}

export function useKnowledgeBasesInfinite(slug: string, params: Omit<ListKbParams, "cursor"> = {}) {
  return useInfiniteQuery({
    queryKey: kbKeys.list(slug, params),
    queryFn: ({ pageParam }) =>
      apiRequest<Paged<KnowledgeBase>>("GET", "/api/knowledge-bases", undefined, {
        cursor: pageParam,
        limit: params.limit,
        q: params.q,
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

export function useKnowledgeBase(slug: string, kbId: string | undefined) {
  return useQuery({
    queryKey: kbKeys.detail(slug, kbId ?? ""),
    queryFn: () => apiRequest<KnowledgeBase>("GET", `/api/knowledge-bases/${kbId}`),
    enabled: Boolean(kbId),
  });
}

export function useCreateKnowledgeBase(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKnowledgeBaseRequest) =>
      apiRequest<KnowledgeBase>("POST", "/api/knowledge-bases", body),
    onSuccess: (created) => {
      qc.setQueryData(kbKeys.detail(slug, created.id), created);
      void qc.invalidateQueries({ queryKey: kbKeys.all(slug) });
    },
  });
}

export function useUpdateKnowledgeBase(slug: string, kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateKnowledgeBaseRequest) =>
      apiRequest<KnowledgeBase>("PUT", `/api/knowledge-bases/${kbId}`, body),
    onSuccess: (updated) => {
      qc.setQueryData(kbKeys.detail(slug, kbId), updated);
      void qc.invalidateQueries({ queryKey: kbKeys.all(slug) });
    },
  });
}

export function useDeleteKnowledgeBase(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kbId: string) => apiRequest<void>("DELETE", `/api/knowledge-bases/${kbId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: kbKeys.all(slug) });
    },
  });
}
